// One way of drawing "how full is this person (or team) in this period", used
// by Resource Management, PM Allocation and the Planner so the three read the
// same.
//
// Committed work (Active SOWs) is a solid bar, green below 90% of capacity,
// amber to 100%, red over. Tentative work (Draft and Issued SOWs, weighted by
// win probability) is a separate striped indigo segment after it, so a
// pipeline that may never land is visible without being mistaken for a
// commitment. Work booked into a period with no capacity at all (leave, a
// closure, a non-working day) is a clash, drawn as "!" rather than as a huge
// percentage.

export function loadTone(committed, capacity) {
  if (committed <= 0) return 'none';
  if (capacity <= 0) return 'clash';
  const u = committed / capacity;
  if (u > 1.0001) return 'over';
  if (u >= 0.9) return 'near';
  return 'ok';
}

const FILL = { ok: 'bg-emerald-400', near: 'bg-amber-400', over: 'bg-red-500', clash: 'bg-red-500', none: '' };
const TEXT = { ok: 'text-emerald-700', near: 'text-amber-700', over: 'text-red-700', clash: 'text-red-700', none: 'text-gray-400' };

// Tentative stripes: indigo at two strengths, which reads on white and on the
// dark theme's slate alike.
const STRIPES = {
  backgroundImage: 'repeating-linear-gradient(135deg, rgba(99,102,241,0.75) 0 3px, rgba(99,102,241,0.3) 3px 6px)',
};

const fmt = (n) => (Math.round(n * 10) / 10).toString();

export default function LoadBar({ committed = 0, tentative = 0, capacity = 0, compact = false, showTentative = true }) {
  const tone = loadTone(committed, capacity);
  const scale = Math.max(capacity, committed + (showTentative ? tentative : 0), 0.0001);
  const cPct = Math.min(100, (committed / scale) * 100);
  const tPct = showTentative ? Math.min(100 - cPct, (tentative / scale) * 100) : 0;
  const capPct = capacity > 0 ? Math.min(100, (capacity / scale) * 100) : 0;
  const empty = committed <= 0 && (!showTentative || tentative <= 0);

  return (
    <div className="w-full">
      <div className={`flex items-baseline justify-center gap-0.5 leading-none ${compact ? 'text-[8px]' : 'text-[9px]'}`}>
        {tone === 'clash' ? (
          <span className="font-bold text-red-700" title="Booked when there is no capacity (leave, a closure or a non-working day)">!</span>
        ) : empty ? (
          <span className="text-gray-300">{capacity > 0 ? '·' : ''}</span>
        ) : (
          <span className={`font-semibold ${TEXT[tone]}`}>{fmt(committed)}</span>
        )}
        {!empty && showTentative && tentative > 0 && (
          <span className="text-indigo-700 font-semibold">+{fmt(tentative)}</span>
        )}
        {!compact && capacity > 0 && !empty && <span className="text-gray-400">/{fmt(capacity)}</span>}
      </div>
      <div className="relative mt-0.5 h-1 rounded-full bg-gray-100 overflow-hidden">
        <div className={`absolute inset-y-0 left-0 ${FILL[tone]}`} style={{ width: `${cPct}%` }} />
        {tPct > 0 && <div className="absolute inset-y-0" style={{ left: `${cPct}%`, width: `${tPct}%`, ...STRIPES }} />}
        {capPct > 0 && capPct < 100 && (
          <div className="absolute inset-y-0 w-px bg-gray-500" style={{ left: `${capPct}%` }} title="Capacity" />
        )}
      </div>
    </div>
  );
}

/* ── Heat cells ──────────────────────────────────────────────────────────────
   The same reading as a shaded cell, for a grid where a lot of people and
   weeks are read at once (Resource Management). The cell takes the colour and
   says one number. The thresholds are LoadBar's, with the green split at half
   booked so a light week reads apart from a well used one. Bid work is a
   hatched strip along the bottom of the cell. */

const HEAT = { none: '', light: 'bg-emerald-50', ok: 'bg-emerald-100', near: 'bg-amber-100', over: 'bg-red-100', clash: 'bg-red-100' };
const HEAT_TEXT = { none: 'text-gray-400', light: 'text-emerald-800', ok: 'text-emerald-800', near: 'text-amber-800', over: 'text-red-800', clash: 'text-red-800' };

export function heatTone(committed, capacity) {
  const tone = loadTone(committed, capacity);
  return tone === 'ok' && committed / capacity < 0.5 ? 'light' : tone;
}

/** The background for a heat cell. Put it on the <td>, with `relative`, and LoadHeat inside. */
export function heatCellClass(committed = 0, capacity = 0) {
  return HEAT[heatTone(committed, capacity)];
}

/** What a heat cell says: free days (the default), % used or days booked. */
export function heatFigure({ committed = 0, capacity = 0, leave = 0 }, show = 'free') {
  if (capacity <= 0 && committed <= 0) return leave > 0 ? 'off' : '';
  if (capacity <= 0) return '!';
  if (show === 'pct') return `${Math.round((committed / capacity) * 100)}%`;
  if (show === 'booked') return committed > 0 ? fmt(committed) : '';
  const free = capacity - committed;
  return free < -0.001 ? `${fmt(-free)} over` : fmt(free);
}

export function LoadHeat({ committed = 0, tentative = 0, capacity = 0, leave = 0, show = 'free', showTentative = true, strong = false, children = null }) {
  const tone = heatTone(committed, capacity);
  const text = heatFigure({ committed, capacity, leave }, show);
  return (
    <>
      <div className={`py-1.5 text-center leading-none ${strong ? 'text-[10px] font-extrabold' : 'text-[9.5px] font-bold'} ${text === 'off' ? 'text-sky-700' : HEAT_TEXT[tone]}`}>
        {text === '!'
          ? <span title="Booked with no capacity (leave, a closure or a non-working day)">!</span>
          : text || <span className="text-gray-300 font-normal">·</span>}
      </div>
      {children}
      {showTentative && tentative > 0 && (
        <div className="absolute left-0 bottom-0 h-1" title="Bid work"
             style={{ width: `${capacity > 0 ? Math.min(100, (tentative / capacity) * 100) : 100}%`, ...STRIPES }} />
      )}
    </>
  );
}

/** The key for LoadHeat cells. */
export function LoadHeatLegend({ showTentative = true }) {
  const swatch = (cls, style) => <span className={`inline-block w-3 h-2 rounded-sm border border-gray-200 ${cls}`} style={style} />;
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[8px] text-gray-500">
      <span className="flex items-center gap-1">{swatch('bg-emerald-50')}Under half booked</span>
      <span className="flex items-center gap-1">{swatch('bg-emerald-100')}Half to 90%</span>
      <span className="flex items-center gap-1">{swatch('bg-amber-100')}90 to 100%</span>
      <span className="flex items-center gap-1">{swatch('bg-red-100')}Over capacity</span>
      {showTentative && <span className="flex items-center gap-1">{swatch('', STRIPES)}Bid work (Draft or Issued SOW, at its win probability)</span>}
      <span className="flex items-center gap-1"><span className="font-bold text-red-700">!</span>Booked with no capacity</span>
      <span className="flex items-center gap-1"><span className="font-semibold text-sky-700">off</span>No capacity: leave or a closure</span>
    </div>
  );
}

/** The key that goes with LoadBar, for a page's legend. */
export function LoadLegend({ showTentative = true }) {
  const swatch = (cls, style) => <span className={`inline-block w-3 h-1.5 rounded-sm ${cls}`} style={style} />;
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[8px] text-gray-500">
      <span className="flex items-center gap-1">{swatch('bg-emerald-400')}Under 90% of capacity</span>
      <span className="flex items-center gap-1">{swatch('bg-amber-400')}90 to 100%</span>
      <span className="flex items-center gap-1">{swatch('bg-red-500')}Over capacity</span>
      {showTentative && <span className="flex items-center gap-1">{swatch('', STRIPES)}Bid (Draft or Issued SOW, at its win probability)</span>}
      <span className="flex items-center gap-1"><span className="font-bold text-red-700">!</span>Booked with no capacity: leave, closure or a non-working day</span>
      <span className="flex items-center gap-1"><span className="inline-block w-px h-2 bg-gray-500" />Capacity line</span>
    </div>
  );
}
